import React, { useState, useEffect } from 'react';
import { db, doc, onSnapshot } from '../services/firebase';

/**
 * Default store hours (Barbados, AST = UTC-4, no DST).
 * Array index matches getDay() — 0 = Sun, 6 = Sat.
 * Used as fallback if Firestore data isn't loaded yet.
 */
const DEFAULT_HOURS = [
  { day: 'Sun', enabled: true,  open: '14:00', close: '22:00' },
  { day: 'Mon', enabled: true,  open: '16:00', close: '22:00' },
  { day: 'Tue', enabled: true,  open: '16:00', close: '22:00' },
  { day: 'Wed', enabled: true,  open: '16:00', close: '00:00' },
  { day: 'Thu', enabled: true,  open: '16:00', close: '22:00' },
  { day: 'Fri', enabled: true,  open: '16:00', close: '02:00' },
  { day: 'Sat', enabled: true,  open: '14:00', close: '02:00' },
];

/**
 * Get current Barbados time parts using Intl API.
 */
function getBarbadosParts() {
  const now = new Date();
  const fmt = (opts) => new Intl.DateTimeFormat('en-US', { timeZone: 'America/Barbados', ...opts });
  const dayStr = fmt({ weekday: 'short' }).format(now);
  const dayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return {
    day: dayMap[dayStr] ?? 0,
    hours: Number(fmt({ hour: 'numeric', hour12: false }).format(now)),
    minutes: Number(fmt({ minute: 'numeric' }).format(now)),
    dateStr: (() => {
      const y = fmt({ year: 'numeric' }).format(now);
      const m = fmt({ month: '2-digit' }).format(now);
      const d = fmt({ day: '2-digit' }).format(now);
      return `${y}-${m}-${d}`;
    })(),
  };
}

/**
 * Check if store is currently open based on Firestore data.
 */
function checkIsOpen(hours, override, customDates) {
  const p = getBarbadosParts();
  const nowMins = p.hours * 60 + p.minutes;

  // 1. Manual override takes top priority
  if (override && override.active) {
    if (override.closed) return { isOpen: false, source: 'override' };
    const [oh, om] = (override.open || '00:00').split(':').map(Number);
    const [ch, cm] = (override.close || '23:59').split(':').map(Number);
    const openMins = oh * 60 + om;
    const closeMins = ch * 60 + cm;
    if (closeMins < openMins) {
      return { isOpen: nowMins >= openMins || nowMins <= closeMins, source: 'override' };
    }
    return { isOpen: nowMins >= openMins && nowMins <= closeMins, source: 'override' };
  }

  // 2. Custom date (holiday) override
  if (customDates && customDates.length > 0) {
    // Check yesterday's custom date first for overnight window (e.g., Thu custom 4PM–2AM, now Fri 1AM)
    const yesterdayDate = new Date(Date.now() - 86400000);
    const yDateStr = (() => {
      const fmt = (opts) => new Intl.DateTimeFormat('en-US', { timeZone: 'America/Barbados', ...opts });
      const y = fmt({ year: 'numeric' }).format(yesterdayDate);
      const m = fmt({ month: '2-digit' }).format(yesterdayDate);
      const d = fmt({ day: '2-digit' }).format(yesterdayDate);
      return `${y}-${m}-${d}`;
    })();
    const yesterdayCustom = customDates.find(c => c.date === yDateStr);
    if (yesterdayCustom && yesterdayCustom.enabled) {
      const [yh1, ym1] = yesterdayCustom.open.split(':').map(Number);
      const [yh2, ym2] = yesterdayCustom.close.split(':').map(Number);
      const yOpenMins = yh1 * 60 + ym1;
      const yCloseMins = yh2 * 60 + ym2;
      if (yCloseMins < yOpenMins && nowMins <= yCloseMins) {
        return { isOpen: true, source: 'custom', label: yesterdayCustom.label };
      }
    }

    // Check today's custom date
    const custom = customDates.find(c => c.date === p.dateStr);
    if (custom) {
      if (!custom.enabled) return { isOpen: false, source: 'custom', label: custom.label };
      const [h1, m1] = custom.open.split(':').map(Number);
      const [h2, m2] = custom.close.split(':').map(Number);
      const openMins = h1 * 60 + m1;
      const closeMins = h2 * 60 + m2;
      // Overnight: only check "after open" part — the before-midnight portion
      if (closeMins < openMins) {
        return { isOpen: nowMins >= openMins, source: 'custom', label: custom.label };
      }
      return { isOpen: nowMins >= openMins && nowMins <= closeMins, source: 'custom', label: custom.label };
    }
  }

  // 3. Standard weekly schedule

  // Always check yesterday's overnight window first (e.g., Thu 4PM–2AM, now is Fri 12:30AM)
  const yesterday = hours[(p.day + 6) % 7];
  if (yesterday && yesterday.enabled) {
    const [yoh, yom] = yesterday.open.split(':').map(Number);
    const [ych, ycm] = yesterday.close.split(':').map(Number);
    const yOpenMins = yoh * 60 + yom;
    const yCloseMins = ych * 60 + ycm;
    // Overnight window: close < open means it wraps past midnight
    if (yCloseMins < yOpenMins && nowMins <= yCloseMins) {
      return { isOpen: true, source: 'standard' };
    }
  }

  const today = hours[p.day];
  if (!today || !today.enabled) {
    return { isOpen: false, source: 'standard' };
  }

  const [h1, m1] = today.open.split(':').map(Number);
  const [h2, m2] = today.close.split(':').map(Number);
  const openMins = h1 * 60 + m1;
  const closeMins = h2 * 60 + m2;

  // Handle overnight (e.g., Fri 4 PM – 2 AM) — only the "after open" part
  if (closeMins < openMins) {
    return { isOpen: nowMins >= openMins, source: 'standard' };
  }
  return { isOpen: nowMins >= openMins && nowMins <= closeMins, source: 'standard' };
}

/**
 * Determine next status change text.
 */
function getNextChange(hours, override, customDates, isOpen) {
  const p = getBarbadosParts();
  const nowMins = p.hours * 60 + p.minutes;

  // If override is active, don't show next change (admin-controlled)
  if (override && override.active) return '';

  // Check custom date first
  if (customDates && customDates.length > 0) {
    // If open from yesterday's custom overnight window, show that close time
    if (isOpen) {
      const yesterdayDate = new Date(Date.now() - 86400000);
      const yDateStr = (() => {
        const fmt = (opts) => new Intl.DateTimeFormat('en-US', { timeZone: 'America/Barbados', ...opts });
        return `${fmt({ year: 'numeric' }).format(yesterdayDate)}-${fmt({ month: '2-digit' }).format(yesterdayDate)}-${fmt({ day: '2-digit' }).format(yesterdayDate)}`;
      })();
      const yc = customDates.find(c => c.date === yDateStr);
      if (yc && yc.enabled) {
        const [yh1, ym1] = yc.open.split(':').map(Number);
        const [yh2, ym2] = yc.close.split(':').map(Number);
        if ((yh2 * 60 + ym2) < (yh1 * 60 + ym1) && nowMins <= (yh2 * 60 + ym2)) {
          return `Closes at ${formatTime12(yc.close)}`;
        }
      }
    }
    const custom = customDates.find(c => c.date === p.dateStr);
    if (custom) {
      if (!custom.enabled) return 'Closed today';
      if (isOpen) return `Closes at ${formatTime12(custom.close)}`;
      const [h1, m1] = custom.open.split(':').map(Number);
      if (nowMins < h1 * 60 + m1) return `Opens at ${formatTime12(custom.open)}`;
      return '';
    }
  }

  if (isOpen) {
    const today = hours[p.day];
    if (today && today.enabled) {
      return `Closes at ${formatTime12(today.close)}`;
    }
    // Must be in overnight window from yesterday
    const yesterday = hours[(p.day + 6) % 7];
    if (yesterday) return `Closes at ${formatTime12(yesterday.close)}`;
  } else {
    // Find next opening
    for (let offset = 0; offset < 7; offset++) {
      const checkDay = (p.day + offset) % 7;
      const sched = hours[checkDay];
      if (!sched || !sched.enabled) continue;
      const [h1, m1] = sched.open.split(':').map(Number);
      const openMins = h1 * 60 + m1;
      if (offset === 0 && nowMins < openMins) {
        return `Opens at ${formatTime12(sched.open)}`;
      }
      if (offset > 0) {
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        return `Opens ${dayNames[checkDay]} at ${formatTime12(sched.open)}`;
      }
    }
  }
  return '';
}

function formatTime12(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return m === 0 ? `${h12} ${ampm}` : `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

export default function LiveStatus({ variant = 'default' }) {
  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [override, setOverride] = useState(null);
  const [customDates, setCustomDates] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [info, setInfo] = useState('');

  // Listen to Firestore settings in real-time
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'whapaxx'), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data.storeHours && Array.isArray(data.storeHours)) setHours(data.storeHours);
        if (data.storeHoursOverride) setOverride(data.storeHoursOverride);
        if (data.storeHoursCustom && Array.isArray(data.storeHoursCustom)) setCustomDates(data.storeHoursCustom);
      }
    }, (err) => {
      console.warn('[LiveStatus] Firestore error:', err);
    });
    return () => unsub();
  }, []);

  // Re-check open/closed every 30 seconds + whenever Firestore data changes
  useEffect(() => {
    const update = () => {
      const { isOpen: open } = checkIsOpen(hours, override, customDates);
      setIsOpen(open);
      setInfo(getNextChange(hours, override, customDates, open));
    };
    update();
    const interval = setInterval(update, 30000);
    return () => clearInterval(interval);
  }, [hours, override, customDates]);

  const isCompact = variant === 'compact';

  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: isCompact ? '6px' : '8px',
      padding: isCompact ? '4px 10px' : '6px 14px',
      borderRadius: '20px',
      background: isOpen
        ? 'rgba(34, 197, 94, 0.1)'
        : 'rgba(229, 43, 57, 0.1)',
      border: `1px solid ${isOpen ? 'rgba(34, 197, 94, 0.3)' : 'rgba(229, 43, 57, 0.3)'}`,
      fontSize: isCompact ? '0.7rem' : '0.8rem',
      fontFamily: "'Rajdhani', sans-serif",
      fontWeight: 600,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    }}>
      {/* Pulsing dot */}
      <span style={{
        width: isCompact ? '6px' : '8px',
        height: isCompact ? '6px' : '8px',
        borderRadius: '50%',
        background: isOpen ? '#22c55e' : '#e52b39',
        boxShadow: isOpen
          ? '0 0 8px rgba(34, 197, 94, 0.6)'
          : '0 0 8px rgba(229, 43, 57, 0.6)',
        animation: 'statusPulse 2s ease-in-out infinite',
        flexShrink: 0,
      }} />

      <span style={{ color: isOpen ? '#22c55e' : '#e52b39' }}>
        {isOpen ? 'Open' : 'Closed'}
      </span>

      {info && (
        <span style={{
          color: '#a8b2ff',
          fontSize: isCompact ? '0.6rem' : '0.75rem',
          fontWeight: 500,
          textTransform: 'none',
        }}>
          · {info}
        </span>
      )}

      <style>{`
        @keyframes statusPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.85); }
        }
      `}</style>
    </div>
  );
}
